import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { SortingState } from "@tanstack/react-table";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import usePermission from "CustomHooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import { fetchPendingLabourInward } from "slices/Warehouse/ItemInward2/PendingLabourInward/thunk";

type PendingLabourInwardProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const PendingLabourInward: React.FC<PendingLabourInwardProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Pending Labour Inward";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Need to change this with pending Labour Inward states
  const selectLabourInwards = createSelector(
    (state: any) => state.PendingLabourInward || {},
    (item: any) => ({
      list: item?.list || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading || false,
      error: item?.error || null,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectLabourInwards);

  // Use custom hook to fetch and access permissions
  const { canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );

  // const data = list || [];
  const data = list || [];

  // Changes this with updated reducer
  useEffect(() => {
    dispatch(
      fetchPendingLabourInward({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  // CSV Export
  const handleExport = async () => {
    try {
      const { blob, filename } = await exportLabourInwardCSV(warehouseIdSearch);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  // Last Column Action Button
  // const DynamicAction =
  //   item_inward_permissions?.permissions?.Update === true
  //     ? [
  //         {
  //           header: "Actions",
  //           enableColumnFilter: false,
  //           enableSorting: false,
  //           cell: ({ row }: { row: any }) => (
  //             <ul className="d-flex gap-2 list-unstyled mb-0">
  //               <li>
  //                 <button
  //                   className="btn btn-sm"
  //                   style={{ backgroundColor: "#3F7DDA", color: "#FFF" }}
  //                   onClick={() =>
  //                     navigate("/warehouse-labour-item-inward-grn/", {
  //                       state: { id: row.original.dispatch_id },
  //                     })
  //                   }
  //                 >
  //                   Inward
  //                 </button>
  //               </li>
  //             </ul>
  //           ),
  //         },
  //       ]
  //     : [];

  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: AddCircleOutlineIcon,
        label: "Inward",
        permission: canUpdate,
        onClick: (PendingLabourInward: any) => {
          navigate(
            `/warehouse-labour-item-inward-grn/${PendingLabourInward.dispatch_id}`
          );
        },
        color: "primary",
      },
    ];
    return items;
  }, [navigate, canUpdate]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasAnyMenuItemPermission = menuItems.some(
      (item) => item.permission === true
    );

    if (!hasAnyMenuItemPermission) return [];

    return [
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          stickyRight: true,
        },
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems]);

  // Listing tanstack table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          return row.original.sr_no || "-";
        },
      },

      {
        header: "Dispatch ID",
        accessorKey: "dispatch_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/issue-to-labour/dropdown/dispatch_number/?warehouse_id=${warehouseIdSearch}&dispatch_status=dispatched`,
        cell: ({ row }: { row: any }) => {
          return row.original.dispatch_number;
        },
      },

      {
        header: "Dispatch Date",
        accessorKey: "dispatch_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.dispatch_date
            ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
            : "N/A";
          return `${date}` || "-";
        },
      },
      {
        header: "Labour Name",
        accessorKey: "labour_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/issue-to-labour/dropdown/labour_name/?warehouse_id=${warehouseIdSearch}/&dispatch_status=dispatched`,
        cell: ({ row }: { row: any }) => {
          return row.original.labour_name || "-";
        },
      },
      {
        header: "Remark",
        accessorKey: "remarks",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return (
            <TruncatedTextCell
              text={row.original.remarks}
              maxWidth="300px"
              maxLines={4}
              rowIndex={row.index}
              fallbackText="-"
            />
          );
        },
        
      },

      ...DynamicAction,
    ],
    [dispatch, list, canUpdate, warehouseIdSearch]
  );

  return (
    <Row id="invoiceList">
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={data}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default PendingLabourInward;
function exportLabourInwardCSV(
  warehouse_id: any
): { blob: any; filename: any } | PromiseLike<{ blob: any; filename: any }> {
  throw new Error("Function not implemented.");
}
